import { FormEvent, useCallback, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import trim from 'lodash/trim';
import { useSearchParams } from 'react-router-dom';
import { useEffectOnce } from 'react-use';
import {
    loadCpaShopsAsync,
    loadCpaShopsRecomendationsAsync,
    loadCpaShopsSuggestAsync,
} from '../../../../data-layer/cpa/actions';
import {
    selectCpaShops,
    selectCpaShopsLoading,
    selectCpaShopsRecomendations,
    selectCpaShopsRecomendationsLoading,
    selectCpaShopsSuggest,
} from '../../../../data-layer/cpa/selectors';
import { useDispatch } from '../../../../data-layer/useDispatch';
import { useSelector } from '../../../../data-layer/useSelector';
import { TIMEOUT } from '../constants';

export const useCpaShops = () => {
    const dispatch = useDispatch();

    const [query, setQuery] = useSearchParams();
    const text = query.get('text') ?? '';
    const queryText = trim(text);
    const [isSearchMode, setIsSearchMode] = useState(Boolean(queryText));

    const shops = useSelector(selectCpaShops);
    const isShopsLoading = useSelector(selectCpaShopsLoading);
    const recomendationsShops = useSelector(selectCpaShopsRecomendations);
    const isRecomendationsShopsLoading = useSelector(selectCpaShopsRecomendationsLoading);

    const loadShops = useMemo(
        () =>
            debounce((textValue: string) => {
                dispatch(loadCpaShopsAsync(textValue));
            }, TIMEOUT),
        [dispatch],
    );

    const shopSuggest = useSelector(selectCpaShopsSuggest);
    const loadShopsSuggest = useMemo(
        () =>
            debounce((queryTtext: string) => {
                dispatch(loadCpaShopsSuggestAsync(queryTtext));
            }, TIMEOUT),
        [dispatch],
    );

    const onChangeText = useCallback(
        (event: FormEvent<HTMLInputElement>) => {
            const textValue = event.currentTarget.value;
            const trimTextValue = trim(textValue);

            setQuery({ text: textValue }, { replace: true });

            trimTextValue && loadShopsSuggest(trimTextValue);
        },
        [loadShopsSuggest, setQuery],
    );

    const onApplySuggest = useCallback(
        (textValue: string = '') => {
            setQuery({ text: textValue }, { replace: true });

            const trimTextValue = trim(textValue);

            trimTextValue && loadShops(trimTextValue);

            setIsSearchMode(Boolean(trimTextValue));
        },
        [loadShops, setQuery],
    );

    useEffectOnce(() => {
        if (!queryText) {
            dispatch(loadCpaShopsRecomendationsAsync());

            return;
        }

        loadShopsSuggest(queryText);
        loadShops(queryText);
    });

    return {
        recomendationsShops,
        isRecomendationsShopsLoading,
        shops,
        isShopsLoading,
        text,
        shopSuggest,
        isSearchMode,
        onApplySuggest,
        onChangeText,
    };
};
